import { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../api';

function Btn({ onClick, children, variant='primary', small, disabled, style={} }) {
  const s={primary:{background:'#c9a96e',color:'#1a1208',border:'none'},secondary:{background:'transparent',color:'#8a7055',border:'1px solid #3a2a12'},danger:{background:'#e63c3c22',color:'#e63c3c',border:'1px solid #e63c3c44'},success:{background:'#10b98122',color:'#10b981',border:'1px solid #10b98144'},ghost:{background:'#c9a96e18',color:'#c9a96e',border:'1px solid #c9a96e44'}};
  return <button onClick={onClick} disabled={disabled} style={{...s[variant],borderRadius:8,padding:small?'6px 14px':'10px 22px',fontWeight:700,cursor:disabled?'default':'pointer',fontSize:small?12:14,opacity:disabled?0.5:1,fontFamily:'inherit',...style}}>{children}</button>;
}
function Input({ label, value, onChange, placeholder, type='text', rows }) {
  const s={width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'};
  return (<div style={{marginBottom:14}}>{label&&<label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>}{rows?<textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{...s,resize:'vertical'}}/>:<input type={type} value={value} onChange={onChange} placeholder={placeholder} style={s}/>}</div>);
}
function Modal({ children, onClose, wide }) {
  return (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}><div style={{background:'#1a1208',border:'1px solid #3a2a12',borderRadius:14,padding:32,maxWidth:wide?900:660,width:'100%',maxHeight:'92vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>{children}</div></div>);
}

const ALLERGENS = ['Gluten','Dairy','Nuts','Tree Nuts','Shellfish','Fish','Eggs','Soy','Sesame','Alcohol'];
const DIETARY = ['Vegetarian','Vegan','Gluten-Free','Dairy-Free','Keto','Low-Carb'];
const CATEGORIES = ['Cocktail','Beer','Wine','Shot','Spirit','Non-Alcoholic','Food','Appetizer','Entree','Dessert','Specialty'];
// Top-level grouping: every category rolls up to Drinks or Eats
const CATEGORY_GROUP = { Cocktail:'Drinks', Beer:'Drinks', Wine:'Drinks', Shot:'Drinks', Spirit:'Drinks', 'Non-Alcoholic':'Drinks', Specialty:'Drinks', Food:'Eats', Appetizer:'Eats', Entree:'Eats', Dessert:'Eats' };
function groupOf(category) { return CATEGORY_GROUP[category] || 'Eats'; }
const catColor = { Cocktail:'#a855f7', Beer:'#f59e0b', Wine:'#e63c3c', Shot:'#6366f1', Spirit:'#8b5cf6', 'Non-Alcoholic':'#10b981', Food:'#f97316', Appetizer:'#f97316', Entree:'#f97316', Dessert:'#c9a96e', Specialty:'#06b6d4' };
const allergenIcon = { Gluten:'🌾', Dairy:'🥛', Nuts:'🥜', 'Tree Nuts':'🌰', Shellfish:'🦐', Fish:'🐟', Eggs:'🥚', Soy:'🫘', Sesame:'🌿', Alcohol:'🍺' };
const dietaryColor = { Vegetarian:'#10b981', Vegan:'#10b981', 'Gluten-Free':'#f59e0b', 'Dairy-Free':'#6366f1', Keto:'#e63c3c', 'Low-Carb':'#f97316' };

function ItemCard({ item, onClick }) {
  const cc = catColor[item.category] || '#c9a96e';
  return (
    <div onClick={onClick} style={{background:'#1a1208',border:`1px solid ${cc}33`,borderRadius:12,overflow:'hidden',cursor:'pointer',transition:'transform 0.15s,box-shadow 0.15s'}}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 12px 40px rgba(0,0,0,0.4)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
      {item.image_url && <img src={item.image_url} alt={item.name} style={{width:'100%',height:140,objectFit:'cover'}} onError={e=>e.target.style.display='none'} />}
      <div style={{padding:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
          <span style={{background:cc+'22',color:cc,border:`1px solid ${cc}44`,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700,textTransform:'uppercase'}}>{item.category}</span>
          {item.price > 0 && <span style={{color:'#c9a96e',fontWeight:700,fontSize:14}}>${item.price.toFixed(2)}</span>}
        </div>
        <div style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',fontSize:17,marginBottom:6,lineHeight:1.3}}>{item.name}</div>
        {item.subcategory && <div style={{color:'#8a7055',fontSize:12,marginBottom:6}}>{item.subcategory}</div>}
        {item.description && <div style={{color:'#8a7055',fontSize:12,lineHeight:1.5,marginBottom:10}}>{item.description.slice(0,80)}{item.description.length>80?'...':''}</div>}
        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
          {item.allergens.map(a=><span key={a} style={{background:'#e63c3c18',color:'#e63c3c',border:'1px solid #e63c3c33',borderRadius:4,padding:'1px 6px',fontSize:10}}>{allergenIcon[a]||'⚠️'} {a}</span>)}
          {item.dietary_tags.map(d=><span key={d} style={{background:dietaryColor[d]+'18'||'#10b98118',color:dietaryColor[d]||'#10b981',border:`1px solid ${dietaryColor[d]||'#10b981'}33`,borderRadius:4,padding:'1px 6px',fontSize:10}}>{d}</span>)}
        </div>
      </div>
    </div>
  );
}

function ItemDetail({ item, onClose, isManager, onEdit, onDelete }) {
  const cc = catColor[item.category] || '#c9a96e';
  return (
    <Modal onClose={onClose} wide>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
        <div>
          <span style={{background:cc+'22',color:cc,border:`1px solid ${cc}44`,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700,textTransform:'uppercase'}}>{item.category}</span>
          {item.subcategory && <span style={{color:'#8a7055',fontSize:12,marginLeft:8}}>{item.subcategory}</span>}
        </div>
        {item.price > 0 && <span style={{color:'#c9a96e',fontWeight:700,fontSize:20}}>${item.price.toFixed(2)}</span>}
      </div>

      {item.image_url && <img src={item.image_url} alt={item.name} style={{width:'100%',maxHeight:200,objectFit:'cover',borderRadius:10,marginBottom:16}} onError={e=>e.target.style.display='none'} />}

      <h2 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',fontSize:26,marginBottom:8}}>{item.name}</h2>
      {item.description && <p style={{color:'#d4b896',lineHeight:1.6,marginBottom:20}}>{item.description}</p>}

      {/* Allergens */}
      {item.allergens.length > 0 && (
        <div style={{background:'#e63c3c18',border:'1px solid #e63c3c33',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
          <div style={{color:'#e63c3c',fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>⚠️ Contains Allergens</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {item.allergens.map(a=><span key={a} style={{background:'#e63c3c22',color:'#e63c3c',border:'1px solid #e63c3c44',borderRadius:6,padding:'4px 10px',fontSize:13,fontWeight:600}}>{allergenIcon[a]||'⚠️'} {a}</span>)}
          </div>
        </div>
      )}

      {/* Dietary */}
      {item.dietary_tags.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:16}}>
          {item.dietary_tags.map(d=><span key={d} style={{background:dietaryColor[d]+'22'||'#10b98122',color:dietaryColor[d]||'#10b981',border:`1px solid ${dietaryColor[d]||'#10b981'}44`,borderRadius:6,padding:'4px 10px',fontSize:13,fontWeight:600}}>✓ {d}</span>)}
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {item.ingredients && (
          <div style={{background:'#2a1f0f',borderRadius:10,padding:16}}>
            <div style={{color:'#c9a96e',fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Ingredients</div>
            <div style={{color:'#d4b896',fontSize:13,lineHeight:1.7,whiteSpace:'pre-wrap'}}>{item.ingredients}</div>
          </div>
        )}
        {(item.glass_type || item.garnish) && (
          <div style={{background:'#2a1f0f',borderRadius:10,padding:16}}>
            {item.glass_type && <><div style={{color:'#c9a96e',fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Glass</div><div style={{color:'#d4b896',fontSize:13,marginBottom:10}}>{item.glass_type}</div></>}
            {item.garnish && <><div style={{color:'#c9a96e',fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Garnish</div><div style={{color:'#d4b896',fontSize:13}}>{item.garnish}</div></>}
          </div>
        )}
      </div>

      {item.recipe_steps && (
        <div style={{background:'#2a1f0f',borderRadius:10,padding:16,marginTop:16}}>
          <div style={{color:'#c9a96e',fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10}}>Recipe / Method</div>
          <pre style={{color:'#d4b896',fontSize:13,lineHeight:1.7,whiteSpace:'pre-wrap',fontFamily:'inherit',margin:0}}>{item.recipe_steps}</pre>
        </div>
      )}

      {item.pairings && (
        <div style={{marginTop:16}}>
          <div style={{color:'#8a7055',fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Pairs Well With</div>
          <div style={{color:'#d4b896',fontSize:13}}>{item.pairings}</div>
        </div>
      )}

      {isManager && (
        <div style={{display:'flex',gap:10,marginTop:24,borderTop:'1px solid #3a2a12',paddingTop:20}}>
          <Btn small variant="ghost" onClick={onEdit}>✏️ Edit</Btn>
          <Btn small variant="danger" onClick={onDelete}>Delete</Btn>
          <Btn small variant="secondary" onClick={onClose}>Close</Btn>
        </div>
      )}
      {!isManager && <div style={{marginTop:20}}><Btn variant="secondary" onClick={onClose}>Close</Btn></div>}
    </Modal>
  );
}

function ItemForm({ item, onClose, onSave, isOwner }) {
  const isNew = !item;
  const [form, setForm] = useState({
    name: item?.name||'', category: item?.category||'Cocktail', subcategory: item?.subcategory||'',
    description: item?.description||'', price: item?.price||0,
    allergens: item?.allergens||[], dietary_tags: item?.dietary_tags||[],
    ingredients: item?.ingredients||'', recipe_steps: item?.recipe_steps||'',
    glass_type: item?.glass_type||'', garnish: item?.garnish||'',
    pairings: item?.pairings||'', image_url: item?.image_url||'',
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  function toggleAllergen(a) { setForm(p=>({...p,allergens:p.allergens.includes(a)?p.allergens.filter(x=>x!==a):[...p.allergens,a]})); }
  function toggleDietary(d) { setForm(p=>({...p,dietary_tags:p.dietary_tags.includes(d)?p.dietary_tags.filter(x=>x!==d):[...p.dietary_tags,d]})); }

  async function save() {
    if (!form.name) return setMsg('Name required');
    setLoading(true);
    try {
      let res;
      if (isNew) res = await post('/menu', form);
      else res = await put(`/menu/${item.id}`, form);
      if (res && res.requested) {
        setMsg('✓ Submitted for owner approval');
        setTimeout(()=>{onSave();onClose();},1200);
      } else {
        setMsg('✓ Saved!');
        setTimeout(()=>{onSave();onClose();},800);
      }
    } catch(e){setMsg('Error: '+e.message);}
    finally{setLoading(false);}
  }

  return (
    <Modal onClose={onClose} wide>
      <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:20}}>{isNew?'Add Menu Item':'Edit: '+item.name}</h3>
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:14,marginBottom:0}}>
        <Input label="Name" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. House Margarita" />
        <div><label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Category</label>
          <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,fontFamily:'inherit',marginBottom:14}}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select></div>
        <div><label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Price ($)</label>
          <input type="number" step="0.01" value={form.price} onChange={e=>setForm(p=>({...p,price:parseFloat(e.target.value)||0}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',marginBottom:14}} /></div>
      </div>
      <Input label="Subcategory (optional)" value={form.subcategory} onChange={e=>setForm(p=>({...p,subcategory:e.target.value}))} placeholder="e.g. Signature, Happy Hour, Seasonal" />
      <Input label="Description" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Guest-facing description..." rows={2} />
      <Input label="Ingredients" value={form.ingredients} onChange={e=>setForm(p=>({...p,ingredients:e.target.value}))} placeholder="2 oz Tequila Blanco, 1 oz Triple Sec, ..." rows={3} />
      <Input label="Recipe / Method Steps" value={form.recipe_steps} onChange={e=>setForm(p=>({...p,recipe_steps:e.target.value}))} placeholder="1. Rim glass with salt&#10;2. Combine all ingredients..." rows={5} />
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14}}>
        <Input label="Glass Type" value={form.glass_type} onChange={e=>setForm(p=>({...p,glass_type:e.target.value}))} placeholder="e.g. Rocks glass" />
        <Input label="Garnish" value={form.garnish} onChange={e=>setForm(p=>({...p,garnish:e.target.value}))} placeholder="e.g. Lime wheel, salt rim" />
        <Input label="Pairings" value={form.pairings} onChange={e=>setForm(p=>({...p,pairings:e.target.value}))} placeholder="e.g. Nachos, Tacos" />
      </div>
      <Input label="Image URL (optional)" value={form.image_url} onChange={e=>setForm(p=>({...p,image_url:e.target.value}))} placeholder="https://..." />

      <div style={{marginBottom:16}}>
        <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Allergens (select all that apply)</label>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {ALLERGENS.map(a=>(
            <div key={a} onClick={()=>toggleAllergen(a)} style={{padding:'6px 12px',borderRadius:8,cursor:'pointer',border:`1px solid ${form.allergens.includes(a)?'#e63c3c':'#3a2a12'}`,background:form.allergens.includes(a)?'#e63c3c22':'#2a1f0f',color:form.allergens.includes(a)?'#e63c3c':'#8a7055',fontSize:12}}>
              {allergenIcon[a]||'⚠️'} {a}
            </div>
          ))}
        </div>
      </div>

      <div style={{marginBottom:20}}>
        <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Dietary Tags</label>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {DIETARY.map(d=>(
            <div key={d} onClick={()=>toggleDietary(d)} style={{padding:'6px 12px',borderRadius:8,cursor:'pointer',border:`1px solid ${form.dietary_tags.includes(d)?dietaryColor[d]||'#10b981':'#3a2a12'}`,background:form.dietary_tags.includes(d)?(dietaryColor[d]||'#10b981')+'22':'#2a1f0f',color:form.dietary_tags.includes(d)?dietaryColor[d]||'#10b981':'#8a7055',fontSize:12}}>
              {d}
            </div>
          ))}
        </div>
      </div>

      {msg && <p style={{color:msg.startsWith('✓')?'#10b981':'#e63c3c',fontSize:13,marginBottom:12}}>{msg}</p>}
      <div style={{display:'flex',gap:10}}>
        <Btn onClick={save} disabled={loading||!form.name}>{loading?'Saving...':'Save Item'}</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

export function MenuCatalog({ isManager, isOwner }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [filterGroup, setFilterGroup] = useState('All');
  const [filterAllergenFree, setFilterAllergenFree] = useState([]);
  const [filterDietary, setFilterDietary] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setItems(await get('/menu')); } catch(e){} finally{setLoading(false);}
  }, []);
  useEffect(()=>{load();},[load]);

  async function deleteItem(id) {
    if (!window.confirm('Remove this item from the menu?')) return;
    const res = await del(`/menu/${id}`);
    if (res && res.requested) alert('✓ Deletion submitted for owner approval.');
    setSelected(null);
    load();
  }

  const cats = ['All', ...new Set(items.map(i=>i.category))];
  const filtered = items.filter(i => {
    if (filterGroup !== 'All' && groupOf(i.category) !== filterGroup) return false;
    if (filterCat !== 'All' && i.category !== filterCat) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.description.toLowerCase().includes(search.toLowerCase()) && !i.ingredients.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterAllergenFree.length > 0 && filterAllergenFree.some(a => i.allergens.includes(a))) return false;
    if (filterDietary && !i.dietary_tags.includes(filterDietary)) return false;
    return true;
  });

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div><h2 style={{fontFamily:"'Playfair Display', serif",fontSize:26,color:'#f5e6c8',marginBottom:4}}>Menu Catalog</h2>
        <p style={{color:'#8a7055',fontSize:13}}>Search cocktails, food, allergens, and dietary info</p></div>
        {isManager && <Btn onClick={()=>setShowAdd(true)}>+ Add Item</Btn>}
      </div>

      {showAdd && <ItemForm onClose={()=>setShowAdd(false)} onSave={load} isOwner={isOwner} />}
      {editing && <ItemForm item={editing} onClose={()=>setEditing(null)} onSave={()=>{setEditing(null);load();}} isOwner={isOwner} />}
      {selected && !editing && (
        <ItemDetail item={selected} onClose={()=>setSelected(null)} isManager={isManager}
          onEdit={()=>{setEditing(selected);setSelected(null);}}
          onDelete={()=>deleteItem(selected.id)} />
      )}

      {/* Search and filters */}
      <div style={{background:'#1a1208',border:'1px solid #3a2a12',borderRadius:12,padding:20,marginBottom:24}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search menu items, ingredients..." style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit',marginBottom:14}} />

        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
          {['All','Drinks','Eats'].map(g=>(
            <button key={g} onClick={()=>{setFilterGroup(g);setFilterCat('All');}} style={{background:filterGroup===g?'#c9a96e':'#2a1f0f',border:`1px solid ${filterGroup===g?'#c9a96e':'#3a2a12'}`,color:filterGroup===g?'#1a1208':'#c9a96e',borderRadius:8,padding:'7px 18px',cursor:'pointer',fontSize:13,fontFamily:'inherit',fontWeight:700}}>{g==='Drinks'?'🍹 Drinks':g==='Eats'?'🍕 Eats':'All'}</button>
          ))}
        </div>

        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
          {cats.filter(c => c==='All' || filterGroup==='All' || groupOf(c)===filterGroup).map(c=>(
            <button key={c} onClick={()=>setFilterCat(c)} style={{background:filterCat===c?'#c9a96e22':'#2a1f0f',border:`1px solid ${filterCat===c?'#c9a96e':'#3a2a12'}`,color:filterCat===c?'#c9a96e':'#8a7055',borderRadius:20,padding:'5px 14px',cursor:'pointer',fontSize:12,fontFamily:'inherit',fontWeight:filterCat===c?700:400}}>{c}</button>
          ))}
        </div>

        <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'center'}}>
          <div>
            <span style={{color:'#8a7055',fontSize:11,textTransform:'uppercase',letterSpacing:'0.06em',marginRight:8}}>Exclude allergens:</span>
            {ALLERGENS.slice(0,6).map(a=>(
              <button key={a} onClick={()=>setFilterAllergenFree(p=>p.includes(a)?p.filter(x=>x!==a):[...p,a])} style={{background:filterAllergenFree.includes(a)?'#e63c3c22':'#2a1f0f',border:`1px solid ${filterAllergenFree.includes(a)?'#e63c3c':'#3a2a12'}`,color:filterAllergenFree.includes(a)?'#e63c3c':'#8a7055',borderRadius:20,padding:'4px 10px',cursor:'pointer',fontSize:11,fontFamily:'inherit',marginRight:4}}>{a}</button>
            ))}
          </div>
          <div>
            <span style={{color:'#8a7055',fontSize:11,textTransform:'uppercase',letterSpacing:'0.06em',marginRight:8}}>Dietary:</span>
            {DIETARY.slice(0,4).map(d=>(
              <button key={d} onClick={()=>setFilterDietary(p=>p===d?'':d)} style={{background:filterDietary===d?(dietaryColor[d]||'#10b981')+'22':'#2a1f0f',border:`1px solid ${filterDietary===d?dietaryColor[d]||'#10b981':'#3a2a12'}`,color:filterDietary===d?dietaryColor[d]||'#10b981':'#8a7055',borderRadius:20,padding:'4px 10px',cursor:'pointer',fontSize:11,fontFamily:'inherit',marginRight:4}}>{d}</button>
            ))}
          </div>
        </div>
      </div>

      {loading && <p style={{color:'#5a4030',textAlign:'center',padding:40}}>Loading menu...</p>}
      {!loading && filtered.length === 0 && <p style={{color:'#5a4030',textAlign:'center',padding:40}}>{search||filterCat!=='All'?'No items match your filters.':'No menu items yet. Add them above.'}</p>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:16}}>
        {filtered.map(item=><ItemCard key={item.id} item={item} onClick={()=>setSelected(item)} />)}
      </div>
    </div>
  );
}
